import { AnimatedModalDialog } from '@ardoq/modal';
import { ButtonGroup, IconButton, SecondaryButton } from '@ardoq/button';
import {
  ChartBody,
  getChartBodyProps,
  WidgetRenderDataProps,
} from '../atoms/ChartBody';
import {
  FullscreenChartWidgetContentProps,
  FullscreenWidgetProps,
} from '../../types';
import styled from 'styled-components';
import { DescriptionIcon, IconName } from '@ardoq/icons';
import { FlexBox } from '@ardoq/layout';
import { Header2 } from '@ardoq/typography';
import { MarkdownViewer } from '@ardoq/markdown';
import { s8 } from '@ardoq/design-tokens';
import { type I18nLocale, t } from '@ardoq/i18n';

const ChartContainer = styled(FlexBox).attrs({
  position: 'relative',
  width: 'full',
  justify: 'center',
  align: 'center',
  borderRadius: 'r8',
  flexDirection: 'column',
  boxShadow: 'shadowS',
})`
  height: 70vh;
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--border-subtle);
`;

const NoteContainer = styled(FlexBox).attrs({
  width: 'full',
})`
  height: 70vh;
  overflow: auto;
  padding-right: ${s8};
`;

const Header = styled(FlexBox).attrs({
  width: 'full',
  paddingY: 'medium',
  paddingX: 'large',
  justify: 'space-between',
  align: 'center',
})`
  border-bottom: 1px solid var(--border-subtle);
`;

const NoteWidgetContent = (props: { content: string }) => (
  <NoteContainer>
    <MarkdownViewer content={props.content} sanitationLevel="permissive" />
  </NoteContainer>
);

const ChartWidgetContent = (
  props: FullscreenChartWidgetContentProps & {
    dashboardContainerRef: HTMLDivElement | null;
    i18nLocale: I18nLocale;
  }
) => (
  <ChartContainer>
    {props.chartData ? (
      <ChartBody
        dashboardContainerRef={props.dashboardContainerRef}
        {...getChartBodyProps({
          ...props,
          onDrilldown: props.onWidgetDrilldown,
          dashboardId: props.dashboardId,
          widgetRenderData: props.chartData as WidgetRenderDataProps, // TODO: Fix this cast later
        })}
        i18nLocale={props.i18nLocale}
      />
    ) : null}
  </ChartContainer>
);

export const FullscreenWidgetModal = (
  props: FullscreenWidgetProps & {
    dashboardContainerRef: HTMLDivElement | null;
  }
) => {
  return (
    <AnimatedModalDialog
      isOpen={props.isOpen}
      onEscPress={props.onClose}
      onClickOutside={props.onClose}
    >
      {props.isOpen && (
        <FlexBox
          flexDirection="column"
          minHeight="80vh"
          minWidth="80vw"
          maxWidth="80vw"
          justify="space-between"
          align="stretch"
          backgroundColor="bgDefault"
          position="relative"
          borderRadius="large"
        >
          <Header>
            <Header2 color="textDefault">{props.widgetName}</Header2>
            <ButtonGroup>
              {props.isNonEmptyChartWidget && !props.hasViewpointDatasource && (
                <SecondaryButton
                  aria-disabled={
                    !props.widgetData.permissions?.canExploreDatasource
                  }
                  isDisabled={
                    !props.widgetData.permissions?.canExploreDatasource
                  }
                  onClick={props.openWidgetDataSource}
                >
                  <DescriptionIcon />
                  {props.widgetData.permissions?.canExploreDatasource
                    ? t(
                        props.i18nLocale,
                        'dashboard:fullscreenWidget.openSource',
                        {
                          sourceType: props.widgetData.datasource?.sourceType,
                        },
                        'Open {{sourceType}}'
                      )
                    : t(
                        props.i18nLocale,
                        'dashboard:fullscreenWidget.missingAccess',
                        {
                          sourceType: props.widgetData.datasource?.sourceType,
                        },
                        'Missing {{sourceType}} access'
                      )}
                </SecondaryButton>
              )}
              <IconButton
                onClick={props.onClose}
                label={t(
                  props.i18nLocale,
                  'dashboard:fullscreenWidget.close',
                  'Close'
                )}
                iconName={IconName.CLOSE}
              />
            </ButtonGroup>
          </Header>
          <FlexBox
            width="full"
            position="relative"
            padding="large"
            gap="medium"
          >
            {props.isNoteWidget ? (
              <NoteWidgetContent content={props.widgetData.content ?? ''} />
            ) : props.isNonEmptyChartWidget ? (
              <ChartWidgetContent {...props} i18nLocale={props.i18nLocale} />
            ) : null}
          </FlexBox>
        </FlexBox>
      )}
    </AnimatedModalDialog>
  );
};
